import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { default as LandscapeIcon } from "@mui/icons-material/NoteOutlined";
import { default as PotraitIcon } from "@mui/icons-material/InsertDriveFileOutlined";
import {
  setContentLayers,
  setLayerMedias,
  setTextLayers,
  setBPLRTComponents,
  setGroupedAssetList,
  setContentResolution,
  setCanvasRes,
} from "../../../../store/actions/cmsAction";
import { inputOnlyNumbers } from "../../../../helpers/inputProcessing";
import { ResolutionType } from "../../../../helpers/resolution";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../../customComponent";
import { colorStyling } from "../../../../helpers/color";
import parser from "html-react-parser";

const MAX_PREVIEW_SIZE = 480;

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const StartupDialog = ({
  open,
  handleClose,
  handleQuickFormat,
  handleUpdateInput,
  handleResType,
}) => {
  const dispatch = useDispatch();

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [startFlag, setStartFlag] = useState(false);
  const [widthInput, setWidthInput] = useState(360);
  const [heightInput, setHeightInput] = useState(640);
  const [orientation, setOrientation] = useState("P");
  const [resType, setResType] = useState("");
  const [contentRes, setContentRes] = useState({
    width: 1080,
    height: 1920,
    custom: false,
  });

  const [widthCanvas, setWidthCanvas] = useState(360);
  const [heightCanvas, setHeightCanvas] = useState(640);

  const [resInputHelp, setResInputHelp] = useState(false);
  const [widthMinMaxHelp, setWidthMinMaxHelp] = useState(false);
  const [heightMinMaxHelp, setHeightMinMaxHelp] = useState(false);

  const handleSetOrientation = (value) => {
    setOrientation(value);

    let resSwitchOri;
    if (!contentRes.custom) {
      resSwitchOri = `${contentRes.height} x ${contentRes.width}`;
    } else {
      resSwitchOri = contentRes.label;
    }

    const resChosen = ResolutionType[value][resSwitchOri];
    let updatedWidth = !contentRes.custom ? resChosen.width : contentRes.height;
    let updatedHeight = !contentRes.custom
      ? resChosen.height
      : contentRes.width;
    const custom = resChosen.custom;
    const freeResFlag = resChosen.free;

    setContentRes({
      width: updatedWidth,
      height: updatedHeight,
      orientation: value,
      label: resSwitchOri,
      custom,
      free: freeResFlag,
    });
    setWidthInput(updatedWidth);
    setHeightInput(updatedHeight);
    setWidthCanvas(updatedWidth);
    setHeightCanvas(updatedHeight);
    if (resType !== "") {
      setResType(resSwitchOri);
    }
  };

  const handleSelectedResType = (e) => {
    const selectedRes = e.target.value;
    if (selectedRes?.length === 0) {
      return;
    }

    const resChosen = ResolutionType[orientation][selectedRes];
    const { width, height, custom, free } = resChosen;
    setWidthInput(width);
    setHeightInput(height);
    setResType(selectedRes);
    setContentRes({
      ...contentRes,
      width,
      height,
      custom,
      free,
      label: selectedRes,
    });

    setWidthCanvas(width);
    setHeightCanvas(height);
    setWidthMinMaxHelp(false);
    setHeightMinMaxHelp(false);
    if (free) {
      setResInputHelp(true);
    } else {
      setResInputHelp(false);
    }
  };

  const handleChangeWidthInput = (event) => {
    setWidthMinMaxHelp(false);

    let value = event?.target?.value ?? event;
    const valueString = `${value}`;
    if (!value || valueString.length === 0) {
      setWidthInput(value);
      setWidthMinMaxHelp(true);
      return;
    }

    value = parseInt(value);
    if (value > 50000) {
      setWidthMinMaxHelp(true);
      value = 50000;
    }

    if (value < 50) {
      setWidthMinMaxHelp(true);
    } else {
      setWidthCanvas(value);
    }

    const currentRes = ResolutionType[contentRes.orientation][contentRes.label];
    const updated = { width: contentRes.width, height: contentRes.height };
    const whichRatio = currentRes.width / currentRes.height;

    setWidthInput(value);
    updated["width"] = value;

    if (!contentRes.free) {
      updated["height"] = Math.round(value / whichRatio);
      setHeightInput(updated["height"]);
    }
  };

  const handleChangeHeightInput = (event) => {
    setHeightMinMaxHelp(false);

    let value = event?.target?.value ?? event;
    const valueString = `${value}`;
    if (!value || valueString.length === 0) {
      setHeightInput(value);
      setHeightMinMaxHelp(true);
      return;
    }

    value = parseInt(value);
    if (value > 50000) {
      setHeightMinMaxHelp(true);
      value = 50000;
    }

    if (value < 50) {
      setHeightMinMaxHelp(true);
    } else {
      setHeightCanvas(value);
    }

    const currentRes = ResolutionType[contentRes.orientation][contentRes.label];
    const updated = { width: contentRes.width, height: contentRes.height };
    const whichRatio = currentRes.width / currentRes.height;

    setHeightInput(value);
    updated["height"] = value;

    if (!contentRes.free) {
      updated["width"] = Math.round(value * whichRatio);
      setWidthInput(updated["width"]);
    }
  };

  const handleBack = () => {
    setStartFlag(false);
  };

  const handleSubmit = () => {
    const width = widthInput;
    const height = heightInput;
    let resLabel = `${width} x ${height}`;
    const orientation = width < height ? "P" : "L";
    let resChosen = ResolutionType[orientation][resLabel];
    if (!resChosen) {
      resLabel = `custom0`;
      resChosen = ResolutionType[orientation][resLabel];
    }
    handleResType(resLabel);

    const setupContentRes = {
      width: width,
      height: height,
      orientation: orientation,
      label: resLabel,
      custom: resChosen.custom,
      free: resChosen.free,
    };

    dispatch(setContentResolution(setupContentRes));
    handleUpdateInput({
      width: setupContentRes.width,
      height: setupContentRes.height,
      orientation: orientation,
    });

    const setupCanvasRes = {
      width: width < height ? 360 : 640,
      height: width < height ? 640 : 360,
      orientation: orientation,
    };
    if (setupContentRes.free === true) {
      setupCanvasRes.width = width;
      setupCanvasRes.height = height;
    }
    dispatch(setCanvasRes(setupCanvasRes));

    dispatch(setContentLayers({}));
    dispatch(setLayerMedias({ reset: true }));
    dispatch(setTextLayers({}));
    dispatch(setBPLRTComponents({}));
    dispatch(setGroupedAssetList({ reset: true }));
    handleClose();
  };

  const previewContentStyle = useMemo(() => {
    let previewWidth, previewHeight;
    const aspectRatio = widthCanvas / heightCanvas;

    if (widthCanvas >= heightCanvas) {
      // Landscape or Square: Constrain by Max Width
      previewWidth = MAX_PREVIEW_SIZE;
      previewHeight = previewWidth / aspectRatio;
    } else {
      // Portrait: Constrain by Max Height
      previewHeight = MAX_PREVIEW_SIZE;
      previewWidth = previewHeight * aspectRatio;
    }

    const containerStyle = {
      width: `${previewWidth}px`,
      height: `${previewHeight}px`,
      position: "relative",
      border: "1px solid #777",
      backgroundColor: "#f0f0f0",
      margin: "20px 0",
    };

    return containerStyle;
  }, [widthCanvas, heightCanvas]);

  if (startFlag) {
    return (
      <Dialog maxWidth="lg" fullWidth open={open}>
        <DialogTitle
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {t("startupDialog.title")}
        </DialogTitle>

        <DialogContent
          sx={{
            minHeight: 400,
          }}
        >
          <Box display={"flex"} width={"100%"}>
            <Box width={"50%"}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={2}
                alignItems={"center"}
              >
                <StyledBox>
                  <Box display={"flex"} gap={2} alignItems={"center"}>
                    <InputLabel>{t("designer.orientationLabel")}</InputLabel>
                  </Box>

                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={3}
                    sx={{ mt: "8px" }}
                  >
                    <ButtonBase
                      sx={{
                        p: 2,
                        width: "112px",
                        borderRadius: "8px",
                        border:
                          orientation === "P"
                            ? "2px solid rgba(28, 78, 216, 0.9)"
                            : "2px solid rgba(28, 78, 216, 0.6)",
                        backgroundColor:
                          orientation === "P"
                            ? "rgba(28, 78, 216, 0.1)"
                            : "transparent",
                        ".potraitIcon": {
                          color:
                            orientation === "P"
                              ? colorStyling.primary
                              : colorStyling.black,
                        },
                        ":hover": {
                          border:
                            orientation === "P"
                              ? ""
                              : "2px solid rgba(28, 78, 216, 0.6)",
                          backgroundColor: "rgba(28, 78, 216, 0.1)",
                        },
                      }}
                      onClick={() => {
                        handleSetOrientation("P");
                      }}
                    >
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        alignItems={"center"}
                      >
                        <PotraitIcon
                          className="potraitIcon"
                          fontSize="inherit"
                          color="inherit"
                          sx={{ fontSize: "64px", transform: "scaleX(-1)" }}
                        />
                        <TypographyNormal
                          sx={{
                            color:
                              orientation === "P"
                                ? colorStyling.primary
                                : colorStyling.black,
                          }}
                        >
                          {t("designer.orientationPortrait")}
                        </TypographyNormal>
                      </Box>
                    </ButtonBase>

                    <ButtonBase
                      sx={{
                        p: 2,
                        width: "112px",
                        borderRadius: "8px",
                        border:
                          orientation === "L"
                            ? "2px solid rgba(28, 78, 216, 0.9)"
                            : "2px solid rgba(28, 78, 216, 0.6)",
                        backgroundColor:
                          orientation === "L"
                            ? "rgba(28, 78, 216, 0.1)"
                            : "transparent",
                        ".landscapeIcon": {
                          color:
                            orientation === "L"
                              ? colorStyling.primary
                              : colorStyling.black,
                        },
                        ":hover": {
                          border:
                            orientation === "L"
                              ? ""
                              : "2px solid rgba(28, 78, 216, 0.6)",
                          backgroundColor: "rgba(28, 78, 216, 0.1)",
                        },
                      }}
                      onClick={() => {
                        handleSetOrientation("L");
                      }}
                    >
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        alignItems={"center"}
                      >
                        <LandscapeIcon
                          className="landscapeIcon"
                          fontSize="inherit"
                          color="inherit"
                          sx={{ fontSize: "64px" }}
                        />
                        <TypographyNormal
                          sx={{
                            color:
                              orientation === "L"
                                ? colorStyling.primary
                                : colorStyling.black,
                          }}
                        >
                          {t("designer.orientationLandscape")}
                        </TypographyNormal>
                      </Box>
                    </ButtonBase>
                  </Box>
                </StyledBox>

                <StyledBox>
                  <InputLabel>{t("designer.resolutionLabel")}</InputLabel>

                  <TextField
                    margin="dense"
                    fullWidth
                    variant="outlined"
                    size="small"
                    select
                    SelectProps={{
                      native: true,
                    }}
                    value={resType}
                    onChange={(e) => {
                      handleSelectedResType(e);
                    }}
                  >
                    {resType === "" && (
                      <option value="">{t("designer.resolutionSelect")}</option>
                    )}
                    {Object.keys(ResolutionType[orientation]).map((res) => {
                      const selectedRes = ResolutionType[orientation][res];
                      return (
                        <option value={res} key={res}>
                          {t(`resolution.${selectedRes.id}`)}
                        </option>
                      );
                    })}
                  </TextField>

                  {resType !== "" && (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      sx={{ mt: "4px", ml: 1 }}
                    >
                      <Box display={"flex"} alignContent={"center"} gap={3}>
                        <Box>
                          <InputLabel sx={{ ml: "11px" }}>
                            {t("designer.widthLabel")}
                          </InputLabel>
                          <Box display={"flex"} alignItems={"center"}>
                            <TextField
                              margin="dense"
                              variant="outlined"
                              placeholder="0"
                              size="small"
                              value={widthInput}
                              autoComplete="off"
                              onChange={handleChangeWidthInput}
                              onKeyDown={(e) => inputOnlyNumbers(e)}
                              disabled={!contentRes.custom}
                              InputProps={{
                                sx: {
                                  "& .Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                  },
                                },
                              }}
                              sx={{
                                width: "77px",
                                backgroundColor: contentRes.custom
                                  ? "white"
                                  : "",
                                "& .MuiInputBase-input": {
                                  border:
                                    widthMinMaxHelp && widthInput < 50
                                      ? "1px solid red"
                                      : undefined,
                                },
                              }}
                            />
                            <TypographyLarge sx={{ pl: 1, color: "black" }}>
                              px
                            </TypographyLarge>
                          </Box>
                        </Box>

                        <Box>
                          <InputLabel sx={{ ml: "11px" }}>
                            {t("designer.heightLabel")}
                          </InputLabel>
                          <Box display={"flex"} alignItems={"center"}>
                            <TextField
                              margin="dense"
                              variant="outlined"
                              placeholder="0"
                              size="small"
                              value={heightInput}
                              autoComplete="off"
                              onChange={handleChangeHeightInput}
                              onKeyDown={(e) => inputOnlyNumbers(e)}
                              disabled={!contentRes.custom}
                              InputProps={{
                                sx: {
                                  "& .Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                  },
                                },
                              }}
                              sx={{
                                width: "77px",
                                backgroundColor: contentRes.custom
                                  ? "white"
                                  : "",
                                "& .MuiInputBase-input": {
                                  border:
                                    heightMinMaxHelp && heightInput < 50
                                      ? "1px solid red"
                                      : undefined,
                                },
                              }}
                            />
                            <TypographyLarge sx={{ pl: 1, color: "black" }}>
                              px
                            </TypographyLarge>
                          </Box>
                        </Box>
                      </Box>

                      {(widthMinMaxHelp || heightMinMaxHelp) && (
                        <Box display={"flex"} maxWidth={"280px"}>
                          <TypographySmall
                            sx={{ color: "red", fontWeight: 500 }}
                          >
                            {parser(t("designer.resMinMaxHelp"))}
                          </TypographySmall>
                        </Box>
                      )}

                      {resInputHelp && (
                        <Box display={"flex"} maxWidth={"280px"}>
                          <TypographySmall sx={{ color: "black" }}>
                            {parser(t("designer.resInputHelp"))}
                          </TypographySmall>
                        </Box>
                      )}
                    </Box>
                  )}
                </StyledBox>
              </Box>
            </Box>

            <Box width={"50%"}>
              <Box>
                <h4>
                  {t("formatWizard.previewLabel")}
                  {resType !== "" && `${widthCanvas}x${heightCanvas}`}
                </h4>
                <Box sx={{ pr: 3, width: "fit-content" }}>
                  <Box
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    sx={previewContentStyle}
                  >
                    {resType !== "" &&
                      widthCanvas <= 4000 &&
                      heightCanvas <= 4000 && (
                        <img
                          draggable={false}
                          src={`https://placehold.co/${widthCanvas}x${heightCanvas}`}
                          alt={` `}
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "fill",
                          }}
                        />
                      )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Box>
            <Box display={"flex"} gap={2}>
              <Button variant="outlined" fullWidth onClick={handleBack}>
                {tCommon("backBtn")}
              </Button>

              <Button
                variant="contained"
                fullWidth
                disabled={resType === ""}
                onClick={handleSubmit}
              >
                {tCommon("createBtn")}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog maxWidth="md" open={open}>
      <DialogContent
        sx={{
          width: 500,
          height: 250,
          alignContent: "center",
        }}
      >
        <Box
          width={"100%"}
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={3}
        >
          <Button
            variant="outlined"
            sx={{ width: "244px", height: "50px", boxShadow: 3 }}
            onClick={() => {
              setStartFlag(true);
            }}
          >
            {t("formatWizard.startBtn")}
          </Button>

          <Button
            variant="outlined"
            sx={{ width: "244px", height: "50px", boxShadow: 3 }}
            onClick={() => {
              handleQuickFormat();
            }}
          >
            {t("formatWizard.quickBtn")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StartupDialog;
